<div class="w-full max-w-3xl mx-auto p-4 lg:p-8 pb-[100px]">

  <!-- Header -->
  <div class="mb-10">
    <div class="flex items-center gap-4 mb-3">
      <div>
        <h1 class="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">Krajowy System e-Faktur</h1>
        <p class="text-gray-500 dark:text-gray-400 text-sm lg:text-base">Zarządzaj integracją z KSeF</p>
      </div>
    </div>
  </div>

  <!-- Loading Skeleton -->
  <ng-container *ngIf="isLoading">
    <!-- Status Card Skeleton -->
    <div class="relative overflow-hidden rounded-2xl mb-6 bg-gray-200 dark:bg-gray-700 animate-pulse">
      <div class="p-6 lg:p-8">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-4">
            <div class="w-16 h-16 rounded-2xl bg-gray-300 dark:bg-gray-600"></div>
            <div>
              <div class="h-4 w-32 bg-gray-300 dark:bg-gray-600 rounded mb-2"></div>
              <div class="h-8 w-48 bg-gray-300 dark:bg-gray-600 rounded"></div>
            </div>
          </div>
          <div class="hidden sm:block h-10 w-24 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
        </div>
      </div>
    </div>

    <!-- Cards Grid Skeleton -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
      <div class="bg-card rounded-xl border border-gray-200 dark:border-gray-700/50 p-5 animate-pulse">
        <div class="flex items-start gap-4">
          <div class="w-12 h-12 rounded-xl bg-gray-200 dark:bg-gray-700"></div>
          <div class="flex-1">
            <div class="h-5 w-32 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
            <div class="h-4 w-48 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
      <div class="bg-card rounded-xl border border-gray-200 dark:border-gray-700/50 p-5 animate-pulse">
        <div class="flex items-start gap-4">
          <div class="w-12 h-12 rounded-xl bg-gray-200 dark:bg-gray-700"></div>
          <div class="flex-1">
            <div class="h-5 w-36 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
            <div class="h-4 w-40 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
    </div>
  </ng-container>

  <!-- Loaded Content -->
  <ng-container *ngIf="!isLoading">

    <!-- Alert: synchronizacja wstrzymana -->
    <div *ngIf="enableSubscriptions && isSubscriptionExpired && ksefConnection?.isAuthorized"
         class="mb-6 p-5 rounded-2xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50">
      <div class="flex items-start gap-4">
        <div class="flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
          <mat-icon [svgIcon]="'tabler_outline:clock-pause'" class="!w-5 !h-5 text-white"></mat-icon>
        </div>
        <div class="flex-1 min-w-0">
          <h4 class="text-base font-bold text-amber-800 dark:text-amber-200">Synchronizacja wstrzymana</h4>
          <p class="mt-1 text-sm text-amber-700 dark:text-amber-300/80">
            Automatyczna synchronizacja faktur z KSeF została wstrzymana z powodu nieaktywnej subskrypcji. 
            Po odnowieniu planu synchronizacja wznowi się automatycznie.
          </p>
          <a routerLink="/konto/subskrypcje"
             class="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold transition-colors shadow-sm">
            <mat-icon [svgIcon]="'tabler_outline:rocket'" class="!w-4 !h-4 text-white"></mat-icon>
            Przedłuż subskrypcję
          </a>
        </div>
      </div>
    </div>

    <!-- Status Card - Hero Section -->
    <div class="relative overflow-hidden rounded-2xl mb-4"
         [style.background]="ksefConnection?.isAuthorized 
           ? 'linear-gradient(to bottom right, #10b981, #0d9488)' 
           : 'linear-gradient(135deg, #1e293b, #334155)'">
      <div class="absolute inset-0 bg-grid-white/10"></div>
      <div class="relative p-6 lg:p-8">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-4">
            <div class="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <mat-icon class="icon-size-8 text-white" svgIcon="ksef:ksef-logo"></mat-icon>
            </div>
            <div>
              <p class="text-white/80 text-sm font-medium uppercase tracking-wide m-0">Status połączenia</p>
              <h2 class="text-2xl lg:text-3xl font-bold text-white mt-1 mb-0">
                {{ ksefConnection?.isAuthorized ? 'Połączono z KSeF' : 'Brak połączenia' }}
              </h2>
            </div>
          </div>
          <div class="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm">
            <span class="w-3 h-3 rounded-full animate-pulse" 
                  [ngClass]="{'bg-white': ksefConnection?.isAuthorized, 'bg-white/50': !ksefConnection?.isAuthorized}"></span>
            <span class="text-white font-medium">{{ ksefConnection?.isAuthorized ? 'Aktywne' : 'Nieaktywne' }}</span>
          </div>
        </div>
        
        <!-- Quick Stats (połączony) -->
        <div *ngIf="ksefConnection?.isAuthorized" class="mt-5 flex flex-wrap items-end justify-between gap-4">
          <div class="flex flex-wrap gap-x-8 gap-y-2">
            <div class="flex flex-col">
              <span class="text-white/60 text-xs font-medium">NIP</span>
              <span class="text-white font-semibold">{{ ksefConnection?.nip || '—' }}</span>
            </div>
            <div *ngIf="isCertificateConnection" class="flex flex-col">
              <span class="text-white/60 text-xs font-medium">Certyfikat wygasa</span>
              <span class="text-white font-semibold">
                <date-formatter [date]="ksefConnection?.certificateExpiresAt" emptyText="—"></date-formatter>
              </span>
            </div>
          </div>
          <button *ngIf="isCertificateConnection" 
                  data-tour-id="certyfikat-ksef"
                  class="px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white text-sm font-medium transition-colors flex items-center gap-2"
                  (click)="openCertificateDialog()">
            <mat-icon class="icon-size-4 text-white" [svgIcon]="'tabler_outline:file-certificate'"></mat-icon>
            Zmień certyfikat
          </button>
        </div>

        <!-- Akcja konfiguracji (niepołączony) -->
        <div *ngIf="!ksefConnection?.isAuthorized" class="mt-5">
          <button data-tour-id="certyfikat-ksef"
                  class="px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white text-sm font-medium transition-colors flex items-center gap-2"
                  (click)="openCertificateDialog()">
            <mat-icon class="icon-size-4 text-white" [svgIcon]="'tabler_outline:file-certificate'"></mat-icon>
            Skonfiguruj certyfikat
          </button>
        </div>
      </div>
    </div>

    <!-- Poradnik -->
    <a href="https://pomoc.fakturnia.pl/baza-wiedzy/ksef/konfiguracja-ksef"
       target="_blank" rel="noopener noreferrer"
       class="inline-flex items-center gap-2 text-base font-medium text-primary-600 dark:text-primary-400 hover:underline w-fit mt-3">
      <mat-icon class="icon-size-5" [svgIcon]="'tabler_outline:book'"></mat-icon>
      Poradnik krok po kroku — jak skonfigurować KSeF
      <mat-icon class="icon-size-4 text-gray-400" [svgIcon]="'tabler_outline:external-link'"></mat-icon>
    </a>

    <app-ksef-setup-alert class="block mb-4 mt-4"></app-ksef-setup-alert>
    
    <!-- Error Alert -->
    <div *ngIf="!ksefConnection?.isAuthorized && ksefConnection?.lastError" 
         class="mb-6 mt-6 rounded-xl border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/30 p-4">
      <div class="flex items-start gap-3">
        <div class="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/50 flex items-center justify-center flex-shrink-0">
          <mat-icon class="icon-size-5 text-red-600 dark:text-red-400" [svgIcon]="'tabler_outline:alert-triangle'"></mat-icon>
        </div>
        <div class="flex-1 min-w-0">
          <h4 class="font-semibold text-red-800 dark:text-red-300">Ostatni błąd połączenia</h4>
          <p class="mt-1 text-sm text-red-700 dark:text-red-400 break-words" [matTooltip]="ksefConnection.lastError">{{ translateKsefError(ksefConnection.lastError) }}</p>
          <p *ngIf="ksefConnection?.lastErrorAt" class="mt-2 text-xs text-red-600/70 dark:text-red-500/70">
            {{ ksefConnection.lastErrorAt | date: 'dd.MM.yyyy, HH:mm:ss' }}
          </p>
        </div>
      </div>
    </div>

    <!-- Cards Grid -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">

    <!-- Token autoryzacyjny -->
    <div *ngIf="ksefConnection?.hasToken && !ksefConnection?.hasCertificate" class="bg-card rounded-xl border border-gray-200 dark:border-gray-700/50 p-5 hover:border-primary-500 dark:hover:border-primary-500 hover:shadow-lg transition-all duration-200 cursor-pointer"
         (click)="openTokenDialog()">
      <div class="flex items-start gap-4">
        <div class="w-12 h-12 rounded-xl flex items-center justify-center"
             [ngClass]="{
               'bg-emerald-100 dark:bg-emerald-900/30': ksefConnection?.hasToken,
               'bg-amber-100 dark:bg-amber-900/30': !ksefConnection?.hasToken
             }">
          <mat-icon class="icon-size-6" 
                    [ngClass]="{
                      'text-emerald-600 dark:text-emerald-400': ksefConnection?.hasToken,
                      'text-amber-600 dark:text-amber-400': !ksefConnection?.hasToken
                    }"
                    [svgIcon]="ksefConnection?.hasToken ? 'tabler_outline:key' : 'tabler_outline:key-off'"></mat-icon>
        </div>
        <div class="flex-1">
          <h3 class="font-semibold text-gray-900 dark:text-white">Token autoryzacyjny</h3>
          <p class="text-sm mt-1" 
             [ngClass]="{
               'text-emerald-600 dark:text-emerald-400': ksefConnection?.hasToken,
               'text-amber-600 dark:text-amber-400': !ksefConnection?.hasToken
             }">
            {{ ksefConnection?.hasToken ? (isTokenConnection ? 'Aktywna metoda połączenia' : 'Token zapisany (nieaktywny)') : 'Kliknij, aby dodać token' }}
          </p>
          <p class="text-xs mt-1.5 text-orange-600 dark:text-orange-400 flex items-center gap-1">
            <mat-icon class="icon-size-3.5 text-orange-600 dark:text-orange-400 flex-shrink-0" [svgIcon]="'tabler_outline:alert-triangle'"></mat-icon>
            Połączenie tą metodą zostanie wyłączone przez KSeF 1 stycznia 2027 roku.
          </p>
        </div>
        <mat-icon class="icon-size-5 text-gray-400" [svgIcon]="'tabler_outline:chevron-right'"></mat-icon>
      </div>
    </div>

  </div>

  <!-- Diagnostyka Section -->
  <div *ngIf="ksefConnection?.isAuthorized" class="mt-4 bg-card rounded-xl border border-gray-200 dark:border-gray-700/50 p-5">
    <div class="flex items-center gap-3 mb-4">
      <div class="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
        <mat-icon class="icon-size-5 text-gray-600 dark:text-gray-400" [svgIcon]="'tabler_outline:settings-cog'"></mat-icon>
      </div>
      <div>
        <h3 class="font-semibold text-gray-900 dark:text-white">Diagnostyka</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400">Sprawdź status połączenia z KSeF</p>
      </div>
    </div>
    
    <div *ngIf="ksefConnection?.lastCheckAt" class="mb-4 p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50">
      <div class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
        <mat-icon class="icon-size-4" [svgIcon]="'tabler_outline:clock'"></mat-icon>
        <span>Ostatnia weryfikacja: <date-formatter [date]="ksefConnection.lastCheckAt" [showTime]="true" emptyText="Nigdy"></date-formatter></span>
      </div>
    </div>

    <div class="flex flex-wrap gap-3">
      <button mat-button (click)="checkConnectionStatus()" [disabled]="isCheckingConnection">
        <mat-icon class="icon-size-4" [ngClass]="{'animate-spin': isCheckingConnection}" [svgIcon]="'tabler_outline:refresh'"></mat-icon>
        <span class="ml-2">{{ isCheckingConnection ? 'Sprawdzanie...' : 'Sprawdź połączenie' }}</span>
      </button>
      <button *ngIf="ksefConnection?.isAuthorized" 
              mat-button 
              color="warn" 
              (click)="deactivateConnection()">
        <mat-icon class="icon-size-4" [svgIcon]="'tabler_outline:unlink'"></mat-icon>
        <span class="ml-2">Dezaktywuj</span>
      </button>
    </div>
  </div>

  <!-- Alert: brakujące uprawnienia KSeF -->
  <div *ngIf="ksefConnection?.isAuthorized && hasMissingPermissions" 
       class="mt-6 rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/30 p-5">
    <div class="flex items-start gap-4">
      <div class="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center flex-shrink-0">
        <mat-icon class="icon-size-5 text-amber-600 dark:text-amber-400" [svgIcon]="'tabler_outline:alert-triangle'"></mat-icon>
      </div>
      <div class="flex-1 min-w-0">
        <h4 class="font-semibold text-amber-800 dark:text-amber-300">Brakujące uprawnienia w KSeF</h4>

        <div *ngIf="!hasInvoiceWritePermission" class="mt-2 flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400">
          <mat-icon class="icon-size-4 text-amber-500 flex-shrink-0 mt-0.5" [svgIcon]="'tabler_outline:x'"></mat-icon>
          <span>Brak uprawnienia do <strong>wysyłki faktur</strong> do KSeF (InvoiceWrite) &mdash; nie będziesz mógł przesyłać faktur.</span>
        </div>

        <div *ngIf="!hasInvoiceReadPermission" class="mt-2 flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400">
          <mat-icon class="icon-size-4 text-amber-500 flex-shrink-0 mt-0.5" [svgIcon]="'tabler_outline:x'"></mat-icon>
          <span>Brak uprawnienia do <strong>pobierania faktur</strong> z KSeF (InvoiceRead) &mdash; nie pobierzesz faktur do naszego systemu.</span>
        </div>

        <p class="mt-3 text-sm text-amber-600 dark:text-amber-500" *ngIf="isCertificateConnection">
          Uprawnienia nie są zaszyte w certyfikacie &mdash; wystarczy nadać brakujące uprawnienia w KSeF dla identyfikatora (NIP), którym się logujesz.
          Nowy certyfikat nie jest potrzebny, jeśli obecny jest ważny i wystawiony na właściwy identyfikator.
        </p>
        <p class="mt-3 text-sm text-amber-600 dark:text-amber-500" *ngIf="!isCertificateConnection">
          Wygeneruj nowy token KSeF i upewnij się, że przyznałeś odpowiednie uprawnienia.
        </p>
      </div>
    </div>
  </div>

  <!-- Synchronizacja faktur Section -->
  <div *ngIf="ksefConnection?.isAuthorized && hasInvoiceReadPermission" 
       class="mt-6 bg-card rounded-xl border border-gray-200 dark:border-gray-700/50 p-5">
    <div class="flex items-center gap-3 mb-4">
      <div class="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
        <mat-icon class="icon-size-5 text-blue-600 dark:text-blue-400" [svgIcon]="'tabler_outline:refresh'"></mat-icon>
      </div>
      <div>
        <h3 class="font-semibold text-gray-900 dark:text-white">Synchronizacja faktur</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400">Faktury pobierane są automatycznie co 10 minut. Możesz również ręcznie pobierać faktury z KSeF z określonego okresu.</p>
      </div>
    </div>

    <!-- Ostatnia synchronizacja info -->
    <div *ngIf="ksefConnection?.lastSyncAt" class="mb-4 p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50">
      <div class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
        <mat-icon class="icon-size-4" [svgIcon]="'tabler_outline:clock'"></mat-icon>
        <span>Ostatnia synchronizacja: {{ ksefConnection.lastSyncAt | date: 'dd.MM.yyyy, HH:mm' }}</span>
        <span *ngIf="ksefConnection.lastSyncInvoicesCount !== undefined" class="text-gray-400">
          · {{ ksefConnection.lastSyncInvoicesCount }} faktur
        </span>
      </div>
    </div>

    <!-- Błąd ostatniej synchronizacji -->
    <div *ngIf="ksefConnection?.lastSyncError" class="mb-4 p-3 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50">
      <div class="flex items-start gap-2">
        <mat-icon class="icon-size-4 text-red-500 flex-shrink-0 mt-0.5" 
                  [svgIcon]="'tabler_outline:alert-triangle'"
                  [matTooltip]="ksefConnection.lastSyncError"></mat-icon>
        <div>
          <p class="text-sm text-red-700 dark:text-red-400">{{ translateKsefError(ksefConnection.lastSyncError) }}</p>
          <p *ngIf="ksefConnection.lastSyncErrorAt" class="text-xs text-red-500/70 mt-1">
            {{ ksefConnection.lastSyncErrorAt | date: 'dd.MM.yyyy, HH:mm:ss' }}
          </p>
        </div>
      </div>
    </div>

    <button mat-button (click)="openSyncDialog()">
      <mat-icon class="icon-size-4" [svgIcon]="'tabler_outline:download'"></mat-icon>
      <span class="ml-2">Synchronizuj faktury</span>
    </button>
  </div>

  <!-- Linki pomocnicze -->
  <div class="mt-6 flex flex-col gap-3">
    <a href="https://ksef.podatki.gov.pl/komunikaty-techniczne/"
       target="_blank" rel="noopener noreferrer"
       class="inline-flex items-center gap-2 text-base text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:underline w-fit">
      <mat-icon class="icon-size-5" [svgIcon]="'tabler_outline:info-circle'"></mat-icon>
      Komunikaty techniczne KSeF
      <mat-icon class="icon-size-4 text-gray-400" [svgIcon]="'tabler_outline:external-link'"></mat-icon>
    </a>
  </div>

  <!-- Permissions Detail -->
  <div *ngIf="ksefConnection?.isAuthorized && ksefConnection?.permissions?.length" 
       class="mt-6 bg-card rounded-xl border border-gray-200 dark:border-gray-700/50 p-5">
    <div class="flex items-center gap-3 mb-4">
      <div class="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
        <mat-icon class="icon-size-5 text-purple-600 dark:text-purple-400" [svgIcon]="'tabler_outline:shield-check'"></mat-icon>
      </div>
      <div>
        <h3 class="font-semibold text-gray-900 dark:text-white">Uprawnienia</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400">Uprawnienia przypisane do Twojego identyfikatora w KSeF</p>
      </div>
    </div>
    
    <div class="flex flex-wrap gap-2">
      <span *ngFor="let permission of ksefConnection?.permissions" 
            class="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
        <mat-icon class="icon-size-4 mr-1.5" [svgIcon]="'tabler_outline:check'"></mat-icon>
        {{ translatePermission(permission) }}
      </span>
    </div>
  </div>

  </ng-container>

</div>